/**
 * "Select from Evidence Base" extraction (template types with
 * `extraction_select_from_evidence_base: true`).
 *
 * A document picked for a template tab is saved as a template-document row with
 * status "Pending" and no data. Extraction supersedes that row with its own
 * Queued -> Processing -> Processed/Error rows, so "has any active row for the template"
 * is the saved selection, and Pending rows must never be read as extraction data or progress.
 */

export const PENDING_SELECTION_STATUS = "Pending";

interface StatusRow {
  status?: string | null;
}

interface TemplateDocumentRow extends StatusRow {
  documentId: string;
}

/** True for a "picked but not yet extracted" placeholder row. */
export const isPendingSelection = (row: StatusRow | null | undefined): boolean =>
  row?.status === PENDING_SELECTION_STATUS;

/**
 * Template-document rows as extraction data: Pending placeholders removed. Returns the input
 * unchanged (same reference) when there is nothing to remove, so memoised consumers don't re-run.
 */
export function withoutPendingSelections<T extends StatusRow>(rows: T[]): T[];
export function withoutPendingSelections<T extends StatusRow>(rows: T[] | undefined): T[] | undefined;
export function withoutPendingSelections<T extends StatusRow>(rows: T[] | undefined): T[] | undefined {
  if (!rows || !rows.some(isPendingSelection)) return rows;
  return rows.filter((row) => !isPendingSelection(row));
}

/** Document ids selected for a template tab: every document with any active row, whatever its status. */
export const buildEvidenceSelectionDocIds = (rows: TemplateDocumentRow[] | undefined): Set<string> =>
  new Set((rows ?? []).map((row) => row.documentId));

/** The grid's documents: only the selected ones when selection mode is on, otherwise all of them. */
export const scopeDocumentsToSelection = <TDoc extends { id: string }>(
  documents: TDoc[],
  selectedDocIds: ReadonlySet<string>,
  selectionMode: boolean
): TDoc[] => (selectionMode ? documents.filter((d) => selectedDocIds.has(d.id)) : documents);

/**
 * Drops grid ticks for documents no longer selected for their template (e.g. removed via the
 * popup), so a removed document can never be extracted. Templates left with nothing are skipped.
 * A template whose rows aren't loaded is left as-is — its ticks can only be on rows it showed.
 */
export const restrictSelectionsToEvidence = <TSel extends { template: { template: { id: string } }; docs: { id: string }[] }>(
  selections: TSel[],
  rowsByTemplateId: Record<string, TemplateDocumentRow[] | undefined>
): TSel[] =>
  selections
    .map((sel) => {
      const rows = rowsByTemplateId[sel.template.template.id];
      if (!rows) return sel;
      const selected = buildEvidenceSelectionDocIds(rows);
      return { ...sel, docs: sel.docs.filter((d) => selected.has(d.id)) };
    })
    .filter((sel) => sel.docs.length > 0);

/** Previously selected document ids with no surviving active row — they need a fresh Pending row. */
export const droppedDocIds = (
  previouslySelected: Iterable<string>,
  remaining: TemplateDocumentRow[] | undefined
): string[] => {
  const remainingIds = buildEvidenceSelectionDocIds(remaining);
  return Array.from(previouslySelected).filter((id) => !remainingIds.has(id));
};

/** What saving the popup changes: ticked but not yet saved, and saved but now unticked. */
export const diffSelection = (
  baseline: ReadonlySet<string>,
  selectedIds: readonly string[]
): { toAdd: string[]; toRemove: string[] } => {
  const selected = new Set(selectedIds);
  return {
    toAdd: selectedIds.filter((id) => !baseline.has(id)),
    toRemove: Array.from(baseline).filter((id) => !selected.has(id)),
  };
};

/**
 * Removing a document whose row is extraction output (not a Pending placeholder or an Error)
 * discards its extracted data, so the popup asks for confirmation first.
 */
export const hasExtractedData = (row: StatusRow | null | undefined): boolean =>
  !!row && !isPendingSelection(row) && row.status !== "Error";

/** A document's active rows for a template — a pick is one row, an extracted document one per subgroup. */
export const rowsForDocument = <TRow extends TemplateDocumentRow>(rows: TRow[] | undefined, documentId: string): TRow[] =>
  (rows ?? []).filter((row) => row.documentId === documentId);

/** Removing this document from the selection would discard extracted data, so ask first. */
export const removalNeedsConfirmation = (rows: TemplateDocumentRow[] | undefined, documentId: string): boolean =>
  rowsForDocument(rows, documentId).some(hasExtractedData);
